import supabase from '../supabaseClient';
import { TABLE_NAMES } from '../config';
import { footageMetaForDualMp4 } from './dualMp4Footage';
import { notifyAdminsFootageUploaded } from './notifyAdminsFootageUploaded';
import { requestCourtAnalysis } from './requestCourtAnalysis';
import { anonSupabaseRequest } from './anonSupabaseRequest';

function getGameTables() {
  return TABLE_NAMES.GAMES === 'Games' ? ['Games'] : ['Games', TABLE_NAMES.GAMES];
}

function parseMetadata(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return { ...raw };
  try {
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

async function updateGameRow(matchId, leftUrl, rightUrl) {
  const { data: existingGame, error: fetchError } = await supabase
    .from(TABLE_NAMES.GAMES)
    .select('extra_players_metadata')
    .eq('match_id', matchId)
    .maybeSingle();

  if (fetchError) throw fetchError;

  const existingMetadata = parseMetadata(existingGame?.extra_players_metadata);
  const updatePayload = {
    upload_later: null,
    game_video_link: leftUrl,
    game_video_link_vimeo: null,
    game_video_link_vimeo_2: rightUrl || null,
    extra_players_metadata: {
      ...existingMetadata,
      vimeo_video_id: null,
      vimeo_embed_url: null,
      footage_upload_pending: false,
      custom_link_type: 'upload',
      ...footageMetaForDualMp4(rightUrl),
    },
  };

  const results = await Promise.all(
    getGameTables().map((tableName) => {
      const payloadForTable = tableName === 'Games'
        ? updatePayload
        : (({ extra_players_metadata, ...rest }) => rest)(updatePayload);
      return supabase.from(tableName).update(payloadForTable).eq('match_id', matchId);
    }),
  );

  const failed = results.find((result) => result.error);
  if (failed?.error) throw failed.error;
}

async function updateViaUploadToken(uploadToken, leftUrl, rightUrl) {
  const rpcBody = {
    upload_token_param: uploadToken,
    vimeo_url: leftUrl,
    vimeo_video_id: null,
    vimeo_embed_url: null,
  };
  if (rightUrl) rpcBody.vimeo_url_2 = rightUrl;

  let updateResult;
  try {
    updateResult = await anonSupabaseRequest('/rest/v1/rpc/update_game_video_via_token', { body: rpcBody });
  } catch (err) {
    if (rightUrl) {
      try {
        updateResult = await anonSupabaseRequest('/rest/v1/rpc/update_game_video_via_token', {
          body: {
            upload_token_param: uploadToken,
            vimeo_url: leftUrl,
            vimeo_video_id: null,
            vimeo_embed_url: null,
          },
        });
      } catch {
        throw new Error(err?.message || 'Failed to update game with video link. Please try again.');
      }
    } else {
      throw new Error(err?.message || 'Failed to update game with video link. Please try again.');
    }
  }

  if (!updateResult || updateResult.length === 0 || !updateResult[0].success) {
    const errorMsg = updateResult?.[0]?.error_message || 'Failed to update game. The link may have been used or expired.';
    throw new Error(errorMsg);
  }
}

/**
 * Attach finished S3 URLs to one or more Games rows (or a shareable upload token).
 */
export async function applyMatchFootageUrls({
  matchIds = [],
  leftUrl,
  rightUrl = null,
  source = 'portal',
  uploadToken = null,
} = {}) {
  if (!leftUrl) {
    throw new Error('Upload finished but no playback URL was returned.');
  }

  if (uploadToken) {
    await updateViaUploadToken(uploadToken, leftUrl, rightUrl);
  } else {
    const ids = Array.from(new Set((matchIds || []).filter(Boolean)));
    if (ids.length === 0) {
      throw new Error('No match to attach footage to.');
    }
    for (const matchId of ids) {
      await updateGameRow(matchId, leftUrl, rightUrl);
    }
  }

  const ids = Array.from(new Set((matchIds || []).filter(Boolean)));
  ids.forEach((matchId) => requestCourtAnalysis(matchId));

  const notifyId = matchIds?.[0] || null;
  if (notifyId) {
    notifyAdminsFootageUploaded({
      matchId: notifyId,
      source,
      uploadToken: uploadToken || null,
    });
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('statbox-footage-ready', {
      detail: { matchIds: matchIds || [], leftUrl, rightUrl },
    }));
  }
}

/**
 * Mark Games rows as waiting for AWS to concat uploaded clip parts.
 * Analysis stays blocked until transloadit-webhook3 writes the stitched URL.
 */
export async function markFootageStitchQueued({
  matchIds = [],
  partCount = 0,
} = {}) {
  const ids = Array.from(new Set((matchIds || []).filter(Boolean)));
  if (ids.length === 0) return;

  for (const matchId of ids) {
    const { data: existingGame, error: fetchError } = await supabase
      .from(TABLE_NAMES.GAMES)
      .select('extra_players_metadata')
      .eq('match_id', matchId)
      .maybeSingle();
    if (fetchError) throw fetchError;

    const existingMetadata = parseMetadata(existingGame?.extra_players_metadata);
    const updatePayload = {
      upload_later: true,
      extra_players_metadata: {
        ...existingMetadata,
        footage_upload_pending: true,
        footage_stitch_pending: true,
        stitch_mp4: true,
        stitch_part_count: partCount || existingMetadata.stitch_part_count || 0,
        dual_mp4: false,
        game_video_link_2: null,
        custom_link_type: 'upload',
      },
    };

    const results = await Promise.all(
      getGameTables().map((tableName) => {
        const payloadForTable = tableName === 'Games'
          ? updatePayload
          : (({ extra_players_metadata, ...rest }) => rest)(updatePayload);
        return supabase.from(tableName).update(payloadForTable).eq('match_id', matchId);
      }),
    );
    const failed = results.find((result) => result.error);
    if (failed?.error) throw failed.error;
  }
}
